import React from 'react';
import { colors, typography, spacing } from '../../styles/design-system';

type LoadingVariant = 'inline' | 'overlay' | 'fullscreen' | 'skeleton';

interface LoadingStateProps {
  loading?: boolean;
  variant?: LoadingVariant;
  message?: string;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

interface EmptyStateProps {
  message?: string;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  style?: React.CSSProperties;
}

interface ErrorStateProps {
  message?: string;
  onRetry?: () => void;
  style?: React.CSSProperties;
}

// Loading State Component
const LoadingState: React.FC<LoadingStateProps> = ({
  loading = true,
  variant = 'inline',
  message = '로딩 중...',
  children,
  style,
}) => {
  if (!loading && children) {
    return <>{children}</>;
  }

  if (!loading) {
    return null;
  }

  switch (variant) {
    case 'overlay':
      return (
        <div style={{ ...styles.overlayContainer, ...style }}>
          {children}
          <div style={styles.overlay}>
            <div style={styles.spinner} />
            {message && <span style={styles.message}>{message}</span>}
          </div>
        </div>
      );

    case 'fullscreen':
      return (
        <div style={{ ...styles.fullscreen, ...style }}>
          <div style={styles.spinner} />
          {message && <span style={styles.message}>{message}</span>}
        </div>
      );

    case 'skeleton':
      return (
        <div style={{ ...styles.skeleton, ...style }}>
          <div style={styles.skeletonLine} />
          <div style={{ ...styles.skeletonLine, width: '60%' }} />
          <div style={{ ...styles.skeletonLine, width: '80%' }} />
        </div>
      );

    case 'inline':
    default:
      return (
        <div style={{ ...styles.inline, ...style }}>
          {message}
        </div>
      );
  }
};

// Empty State Component
export const EmptyState: React.FC<EmptyStateProps> = ({
  message = '데이터가 없음.',
  icon,
  action,
  style,
}) => {
  return (
    <div style={{ ...styles.emptyContainer, ...style }}>
      {icon && <div style={styles.emptyIcon}>{icon}</div>}
      <p style={styles.emptyMessage}>{message}</p>
      {action && (
        <button style={styles.emptyAction} onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
};

// Error State Component
export const ErrorState: React.FC<ErrorStateProps> = ({
  message = '오류가 발생했음.',
  onRetry,
  style,
}) => {
  return (
    <div style={{ ...styles.errorContainer, ...style }}>
      <p style={styles.errorMessage}>{message}</p>
      {onRetry && (
        <button style={styles.retryButton} onClick={onRetry}>
          다시 시도
        </button>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  inline: {
    textAlign: 'center',
    padding: spacing[10],
    color: colors.gray[600],
    fontSize: typography.fontSize.base,
  },
  overlayContainer: {
    position: 'relative',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[3],
    zIndex: 10,
  },
  fullscreen: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[3],
    zIndex: 9999,
  },
  spinner: {
    width: '24px',
    height: '24px',
    border: `2px solid ${colors.gray[200]}`,
    borderTopColor: colors.black,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  message: {
    color: colors.gray[600],
    fontSize: typography.fontSize.base,
  },
  skeleton: {
    padding: spacing[5],
  },
  skeletonLine: {
    height: '16px',
    backgroundColor: colors.gray[200],
    marginBottom: spacing[3],
    borderRadius: '4px',
    animation: 'pulse 1.5s infinite',
  },
  emptyContainer: {
    textAlign: 'center',
    padding: spacing[10],
  },
  emptyIcon: {
    marginBottom: spacing[4],
    fontSize: '48px',
    color: colors.gray[400],
  },
  emptyMessage: {
    color: colors.gray[500],
    fontSize: typography.fontSize.base,
    marginBottom: spacing[5],
  },
  emptyAction: {
    padding: `${spacing[2]} ${spacing[5]}`,
    fontSize: typography.fontSize.base,
    color: colors.black,
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray[300]}`,
    cursor: 'pointer',
  },
  errorContainer: {
    textAlign: 'center',
    padding: spacing[10],
  },
  errorMessage: {
    color: colors.status.error,
    fontSize: typography.fontSize.base,
    marginBottom: spacing[5],
  },
  retryButton: {
    padding: `${spacing[2]} ${spacing[5]}`,
    fontSize: typography.fontSize.base,
    color: colors.white,
    backgroundColor: colors.black,
    border: 'none',
    cursor: 'pointer',
  },
};

export default LoadingState;
